import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { DerivedDataObject, Instance, Resize, SortingRule } from 'react-table-6';
import { getMediaHistory, getSearchMediaTasks, getSearchResultsForAgentQueue } from '../api';
import { callTranscriptionUnSubscribe } from '../api/transcriptions';
import { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { ClidObjectType, ContactType, EntityType, ExtensionStatus, MediaCallHistoryEntry, MediaHistoryIpfxType, MediaType, QueueEmail, SearchEntity, Task, TaskItemAction } from '../model';
import { actions } from '../store';
import { selectConfigProp, selectDateFormating, selectMyAccess, selectMyself } from '../store/reducers/config';
import { ColumnWidths, getGridSortOrder, MediaTypesFilter, selectGridColumnWidths, selectSearchFilters } from '../store/reducers/localSettings';
import { selectMyActiveMedia } from '../store/reducers/media';
import { selectExtension } from '../store/reducers/queueAgents';
import { selectAdvancedSearchQueueEmails, selectSearchEmailAccounts } from '../store/reducers/queueEmails';
import { TableColumn, TableRow } from '../table-utils';
import { formatRelativeDateTime } from './AgentsTable';
import { CallViewByItemDetails } from './AgentView/CallDetailsPanel';
import { ChatViewPanel } from './AgentView/ChatViewPanel';
import { ContactForm, ContactLookup } from './AgentView/ContactPage';
import EmailViewPanel from './AgentView/EmailViewPanel';
import { FacebookViewPanel } from './AgentView/FacebookViewPanel';
import { OutBoundViewPanel } from './AgentView/OutboundViewPanel';
import { TranscriptionViewPanel } from './AgentView/Transcription';
import { TweetViewPanel } from './AgentView/TweatViewPanel';
import Autocomplete from './Autocomplete';
import Button from './Button';
import Checkbox from './Checkbox';
import DatePicker from './DatePicker';
import { ReactComponent as DialIcon } from './icons/dial-desk.svg';
import { ReactComponent as MissedIcon } from './icons/history/missed.svg';
import { ReactComponent as PlacedIcon } from './icons/history/placed.svg';
import { ReactComponent as ReceivedIcon } from './icons/history/received.svg';
import { ReactComponent as EmailReplyIcon } from './icons/history/reply-email.svg';
import { ReactComponent as MediaCallBackIcon } from './icons/media/callback.svg';
import { ReactComponent as EmailIcon } from './icons/media/email.svg';
import { ReactComponent as FacebookIcon } from './icons/media/facebook.svg';
import { ReactComponent as TweetIcon } from './icons/media/tweet.svg';
import { ReactComponent as WebChatIcon } from './icons/media/web-chat.svg';
import { ReactComponent as ExtensionViewIcon } from './icons/toolbar-agent.svg';
import { ReactComponent as QueueViewIcon } from './icons/toolbar-queue.svg';
import Input from './Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import Modal from './Modal';
import MoreButton from './MoreButton';
import './SearchPage.scss';
import SidebarView from './SidebarView';
import { SplitterDivider, StoredSplitter } from './Splitter';
import { TabButton, TabsHeader } from './Tabs';

interface MediaMenuState {
  target: MenuTarget
  item: MediaCallHistoryEntry
}

export interface ItemSelected {
  type: MediaType
  id: string
  isOutgoing: boolean
  notes?: string
  ipfxId?: string,
  ipfxType?: MediaHistoryIpfxType,
  ipfxDescription?: string,
  accountId?: string,
  queueId?: string,
}

export type MediaColumnSort = 'MediaTypeId' | 'ExtensionName' | 'Subject' | 'QueueName' | 'DateUtc' | 'ReceivedAtUtc'

export default function SearchPage() {
  console.log('Render Search Page')

  const dispatch = useDispatch()
  const [menu, setMenu] = useState<MediaMenuState>()
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const myId = useSelector(selectMyself);
  const types = useSelector(selectSearchFilters).types
  const media = useSelector(selectMyActiveMedia)
  const reactTable = useRef<ReactTable<MediaCallHistoryEntry>>(null);
  const emails = useSelector(selectAdvancedSearchQueueEmails)

  const mediaQueueTwitter = useSelector(s => selectConfigProp(s, 'mediaQueueTwitter'))
  const mediaQueueFacebook = useSelector(s => selectConfigProp(s, 'mediaQueueFacebook'))
  const mediaQueueEmail = useSelector(s => selectConfigProp(s, 'mediaQueueEmail'))
  const mediaQueueWebChat = useSelector(s => selectConfigProp(s, 'mediaQueueWebChat'))

  // filters
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState<number | null>(null)
  const [endDate, setEndDate] = useState<number | null>(null)
  const [contactId, setContactId] = useState<string>()
  const [contactDescr, setContactDescr] = useState('')
  const [accountDescr, setAccountDescr] = useState('')
  const [account, setAccount] = useState<QueueEmail>()
  const [agentDescr, setAgentDescr] = useState('')
  const [agent, setAgent] = useState<SearchEntity>()
  const [queue, setQueue] = useState<SearchEntity>()
  const [taskDescr, setTaskDescr] = useState('')
  const [task, setTask] = useState<Task>()
  const format = useSelector(selectDateFormating)

  const filters = useSelector(selectSearchFilters)

  //paging
  const [data, setData] = useState<MediaCallHistoryEntry[]>()
  const [loading, setLoading] = useState(false)
  const gridType = 'search'
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()
  const s = useSelector(s => getGridSortOrder(s, gridType))
  const [sorted, setSorted] = useState<SortingRule[]>([s[0]])
  const lastReq = useRef(0)

  //selected item
  const item = selected && data && data.find(d => d.id === selected)

  // media first load
  const refFired = useRef<string>('')

  useEffect(() => {
    if (!media) { return }
    if (![ClidObjectType.personalContact, ClidObjectType.companyContact, ClidObjectType.customerContact].includes(media.clidObjectType)) { return }

    if (refFired.current === media.id) {
      return
    }

    if (data || contactId !== undefined || contactDescr !== '') {
      return
    }

    setContactId(media.clidObjectKey)
    setContactDescr(media.clidDescription)

    refFired.current = media.id

  }, [contactDescr, contactId, data, media])

  useEffect(() => {
    return () => {
      setSelected(undefined)
      dispatch(actions.clearSelectedNumber())
      dispatch(actions.clearAdvancedSearchResults())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onKeyDown(e: KeyboardEvent) {
    if (e.target instanceof HTMLElement) {
      const element = e.target as HTMLElement
      if (element.id.indexOf("mce") === 0) {
        return
      }
    }
    if (data == null || data.length === 0 || reactTable.current === null) return

    const table = reactTable.current as Instance
    const sorted: MediaCallHistoryEntry[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)

    function selectFirstRow() {
      if (!selected && sorted.length > -1) {
        const first = sorted[0]
        setSelected(first.id)
        return true
      }
      return false
    }

    function selectNextUp() {
      const index = sorted.findIndex(d => selected !== null && selected === d.id)
      return index - 1
    }

    function selectNextDown() {
      const index = sorted.findIndex(d => selected !== null && selected === d.id)
      return index + 1
    }

    switch (e.keyCode) {
      case 38://up
        e.preventDefault()

        if (selectFirstRow()) return

        const prevIndex = selectNextUp()
        if (prevIndex < 0) return
        const prev = sorted[prevIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //

        } else {
          if (prev.mediaTypeId === 'CALL') {
            dispatch(actions.setSelectedNumber(prev.subject))
          } else {
            dispatch(actions.clearSelectedNumber())
          }
          setSelected(prev.id)
        }
        break
      case 40://down
        e.preventDefault()

        if (selectFirstRow()) return

        const nextIndex = selectNextDown()
        if (nextIndex >= sorted.length) return
        const next = sorted[nextIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //
        } else {
          if (next.mediaTypeId === 'CALL') {
            dispatch(actions.setSelectedNumber(next.subject))
          } else {
            dispatch(actions.clearSelectedNumber())
          }
          setSelected(next.id)
        }

        break
    }
  }

  const onKeyDownRef = useRef(onKeyDown)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  })

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      onKeyDownRef.current(e)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const onColumnResize = useCallback((newResized: Resize[], event: any) => {
    newResized.forEach(element => {
      if (refColumnResizeState.current) {
        refColumnResizeState.current = { ...refColumnResizeState.current, [element.id]: element.value }
      } else {
        refColumnResizeState.current = { [element.id]: element.value }
      }
    });
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  const runSearch = useCallback(async () => {
    if (!myId || !emails) { return }

    if (startDate === null) {
      dispatch(actions.showClientNotification("You need to supply a start date"))
      return
    }

    if (endDate === null) {
      dispatch(actions.showClientNotification("You need to supply an end date"))
      return
    }

    const reqId = ++lastReq.current

    setLoading(true)
    setSelected(undefined)

    if ((!account && accountDescr !== "") || (account && accountDescr !== account.emailAddress && accountDescr !== account.azureSearchGroup)) {
      setAccountDescr('')
      dispatch(actions.showClientNotification('Invalid email account or group'))
    }

    if ((!agent && !queue && agentDescr !== "") || (agent !== undefined && agentDescr !== agent.description && !queue) || (queue !== undefined && agentDescr !== queue.description && !agent)) {
      setAgentDescr('')
      dispatch(actions.showClientNotification('Invalid agent or queue'))
    }

    if ((!task && taskDescr !== "") || (task !== undefined && taskDescr !== taskDescription(task))) {
      setTaskDescr('')
      dispatch(actions.showClientNotification('Invalid task'))
    }

    const cId = contactId || ''
    const cDescr = !contactId ? contactDescr : ''
    const accounts = account && account.type === 0 ? [account] : []
    const group = account && account.type === 1 ? emails.filter(x => x.azureSearchGroup === account.azureSearchGroup) : []

    const data = await getMediaHistory(myId.id, searchTerm, startDate, endDate, 100, types, cId, cDescr, accounts.concat(group).map(x => x.accountId), agent ? agent.entityId : '', queue ? queue.entityId : '', task ? task.taskId : '')
    if (lastReq.current !== reqId) { return }

    //save data for shortcut keys
    dispatch(actions.fetchAdavncedSearchResponse(data))

    setData(data.rows)
    setLoading(false)

    dispatch(actions.clearSelectedNumber())
    setSelected(undefined)
  }, [account, accountDescr, agent, agentDescr, contactDescr, contactId, dispatch, emails, endDate, myId, queue, searchTerm, startDate, task, taskDescr, types])


  const onMediaType = useCallback((type: keyof typeof types, value: boolean) => {
    dispatch(actions.updateSearchTypeFilter({ ...types, [type]: value }))
  }, [dispatch, types])

  const now = new Date()
  const today = dayjs(now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate())
  const sevenDays = today.add(-7, 'days').valueOf()
  if (filters.months > 0) {
    const sd = dayjs(today).add((filters.months * -1), 'months').valueOf()
    if (sd !== startDate) {
      setStartDate(sd)
    }
  } else if (filters.startDate) {
    if (filters.startDate !== startDate) {
      setStartDate(filters.startDate)
    }
  } else if (startDate !== sevenDays) {
    setStartDate(sevenDays)
  }

  if (filters.months > 0) {
    if (endDate !== today.valueOf()) {
      setEndDate(today.valueOf())
    }
  } else if (filters.endDate) {
    if (filters.endDate !== endDate) {
      setEndDate(filters.endDate)
    }
  } else if (endDate !== today.valueOf()) {
    setEndDate(today.valueOf())
  }

  const rendor = useMemo(() => {
    const shortTimeFormat = format ? format.shortTimeFormat : ''
    const shortDateFormat = format ? format.shortDateFormat : ''
    const table = <ReactTable ref={reactTable}
      className='MediaSearch'
      manual
      data={data}
      loading={loading}
      showPagination={false}
      minRows={0}
      multiSort={false}
      sorted={sorted}
      columns={getMediaColumns(shortTimeFormat, shortDateFormat, setMenu, widths)}
      getTrProps={(_: any, row?: TableRow<MediaCallHistoryEntry>): React.HtmlHTMLAttributes<HTMLTableRowElement> | undefined => row && ({
        className: row && (selected && row.original.id === selected) || (menu && row.original.id === menu.item.id) ? 'selected' : undefined,
        onContextMenu(e) {
          setMenu({ target: targetFromEvent(e), item: row.original })
        },
        onClick() {
          if (row.original.mediaTypeId === 'CALL') {
            dispatch(actions.setSelectedNumber(row.original.subject))
          } else {
            dispatch(actions.clearSelectedNumber())
          }

          setSelected(row.original.id)
        },
      })}
      noDataText={data && !loading ? undefined : null}
      onFetchData={(state, instance) => {
        if (data && data.length) { runSearch() }
      }}
      onSortedChange={(newSort, column) => {
        setSorted(newSort)
        dispatch(actions.requestSetGridSortOrder(gridType, [newSort[0]]))
      }}
      onResizedChange={onColumnResize} />

    return <><SidebarView className='SearchPage' value={0} onChange={() => { }}>
      <SidebarView.Sidebar className='sidebar'>
        <SidebarView.Header>Filters</SidebarView.Header>
        <FilterSection title='Date' className='dates'>
          <div className='links'>
            <Button primary={filters.months === 1} onClick={() => dispatch(actions.updateSearchMonthsFilter(1))}>1m</Button>
            <Button primary={filters.months === 3} onClick={() => dispatch(actions.updateSearchMonthsFilter(3))}>3m</Button>
            <Button primary={filters.months === 6} onClick={() => dispatch(actions.updateSearchMonthsFilter(6))}>6m</Button>
            <Button primary={filters.months === 12} onClick={() => dispatch(actions.updateSearchMonthsFilter(12))}>1y</Button>
            <Button primary={filters.months === 24} onClick={() => dispatch(actions.updateSearchMonthsFilter(24))}>2y</Button>
          </div>
          <DatePicker placeholder='Start' value={startDate} onChange={(x) => {
            dispatch(actions.updateSearchStartDateFilter(x))
            //setStartDate(x)
          }} />
          <DatePicker placeholder='End' value={endDate} onChange={(x) => {
            dispatch(actions.updateSearchEndDateFilter(x))
            //setEndDate(x)
          }} />
        </FilterSection>
        <FilterSection title='Media type'>
          <MediaTypeCheckbox types={types} type='CALL' label='Calls' onChange={onMediaType} />
          {false && <MediaTypeCheckbox types={types} type='VM' label='Callbacks' onChange={onMediaType} />}
          {mediaQueueEmail && <MediaTypeCheckbox types={types} type='EMAIL' label='Emails' onChange={onMediaType} />}
          {mediaQueueWebChat && false && <MediaTypeCheckbox types={types} type='TC' label='Webchat' onChange={onMediaType} />}
          {mediaQueueFacebook && false && <MediaTypeCheckbox types={types} type='FB' label='Facebook' onChange={onMediaType} />}
          {mediaQueueTwitter && false && <MediaTypeCheckbox types={types} type='TWEET' label='Twitter' onChange={onMediaType} />}
        </FilterSection>

        <FilterSection title='Direction'>
          <MediaTypeCheckbox types={types} type='DIRECTIONIN' label='Incoming' onChange={(x, c) => {
            if (c === false && types.DIRECTIONOUT === false) {
              dispatch(actions.updateSearchTypeFilter({ ...types, 'DIRECTIONIN': c, 'DIRECTIONOUT': !c }))
            } else {
              dispatch(actions.updateSearchTypeFilter({ ...types, 'DIRECTIONIN': c }))
            }
          }} />
          <MediaTypeCheckbox types={types} type='DIRECTIONOUT' label='Outgoing' onChange={(x, c) => {
            if (c === false && types.DIRECTIONIN === false) {
              dispatch(actions.updateSearchTypeFilter({ ...types, 'DIRECTIONOUT': c, 'DIRECTIONIN': !c }))
            } else {
              dispatch(actions.updateSearchTypeFilter({ ...types, 'DIRECTIONOUT': c }))
            }

          }} />
        </FilterSection>

        {types.EMAIL && <FilterSection title='Email'>
          <MediaTypeCheckbox types={types} type='EMAILAUTO' label='Include auto replies' onChange={onMediaType} />
          <MediaTypeCheckbox types={types} type='EMAILATTACHMENTS' label='Must have attachments' onChange={onMediaType} />
        </FilterSection>}

        <FilterSection>
          <Button className='clear-filter' onClick={() => {
            setContactId(undefined)
            setContactDescr('')

            setAccount(undefined)
            setAccountDescr('')

            setAgent(undefined)
            setQueue(undefined)
            setAgentDescr('')

            setTask(undefined)
            setTaskDescr('')

            dispatch(actions.resetSearchFilter({ CALL: true, VM: true, EMAIL: mediaQueueEmail, FB: mediaQueueFacebook, TWEET: mediaQueueTwitter, TC: mediaQueueWebChat, EMAILAUTO: false, EMAILATTACHMENTS: false, DIRECTIONIN: true, DIRECTIONOUT: true }))
          }}>Reset</Button>
        </FilterSection>
      </SidebarView.Sidebar>
      <SidebarView.Body className='body'>
        <SidebarView.Header>
          Advanced search
        </SidebarView.Header>
        <div className='search-options'>
          <div>
            <Input placeholder='Search subject for keywords' className='search-input'
              value={searchTerm} onChange={e => {
                setSearchTerm(e.currentTarget.value)
              }} autocomplete={false} onKeyUp={e => {
                if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                  e.stopPropagation()
                  e.preventDefault()

                  runSearch()

                } else if (e.nativeEvent.code === 'Escape') {
                  setSearchTerm('')
                }

              }} /></div>
          <div></div>
          <div>
            <ContactLookup className='search-input' value={contactDescr} enabled={!media || (media && (media.clidObjectKey !== contactId || media.clidDescription !== contactDescr))} emailRequired={false} localServer={true} includePersonal={false} onChange={c => {
              setContactDescr(c)
              setContactId(undefined)
            }} placeholder='Customer name, email or phone number' onSelected={item => {
              setContactId(item.id)
              setContactDescr(item.description)
              return item.description
            }} onKeyUp={e => {
              if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                e.stopPropagation()
                e.preventDefault()

                runSearch()

              } else if (e.nativeEvent.code === 'Escape') {

                setContactDescr('')
                setContactId(undefined)
              }
            }} /></div>
          <div><TaskLookup className='search-input' value={taskDescr} placeholder='Task'
            onChange={c => {
              setTaskDescr(c)
              if (task && c !== task.subject) {
                setTask(undefined)
              }
            }}
            onSelected={item => {
              const d = taskDescription(item)
              setTask(item)
              setTaskDescr(d)
              return d
            }}
            onKeyUp={e => {
              if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                e.stopPropagation()
                e.preventDefault()

                if (account !== undefined)
                  runSearch()

              } else if (e.nativeEvent.code === 'Escape') {
                setTaskDescr('')
                setTask(undefined)
              }
            }} /></div>
          <div></div>
          <div><AgentQueueLookup className='search-input' value={agentDescr} placeholder='Queue or Agent'
            onChange={c => {
              setAgentDescr(c)
              if (agent && c !== agent.description) {
                setAgent(undefined)
              } else if (queue && c !== queue.description) {
                setQueue(undefined)
              }
            }}
            onSelected={item => {
              if (item.entityType === EntityType.Extension) {
                setAgent(item)
                setQueue(undefined)
              } else if (item.entityType === EntityType.Queue) {
                setAgent(undefined)
                setQueue(item)
              }
              setAgentDescr(item.description)
              return item.description
            }}
            onKeyUp={e => {
              if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                e.stopPropagation()
                e.preventDefault()

                if (account !== undefined)
                  runSearch()

              } else if (e.nativeEvent.code === 'Escape') {
                setAgentDescr('')
                setAgent(undefined)
                setQueue(undefined)
              }
            }} /></div>
          <div>
            <EmailAccountLookup className='search-input' value={accountDescr} placeholder='Email Account or Group'
              onChange={c => {
                setAccountDescr(c)
                if (account && account.type === 0 && c !== account.emailAddress) {
                  setAccount(undefined)
                } else if (account && account.type === 1 && c !== account.azureSearchGroup) {
                  setAccount(undefined)
                }
              }}
              onSelected={item => {
                setAccountDescr(item !== undefined ? item.type === 0 ? item.emailAddress : item.azureSearchGroup : '')
                setAccount(item)
                return item !== undefined ? item.type === 0 ? item.emailAddress : item.azureSearchGroup : ''
              }}
              onKeyUp={e => {
                if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                  e.stopPropagation()
                  e.preventDefault()

                  if (account !== undefined)
                    runSearch()

                } else if (e.nativeEvent.code === 'Escape') {
                  setAccountDescr('')
                  setAccount(undefined)
                }
              }} />
          </div>
          <div></div>
        </div>
        <div className='search-results'>
          <div><h2>{data ? 'Top ' + data.length + ' results returned. ' : 'Search results'}</h2>
            {data && data.length === 100 ? <h6>If you don’t find what you are looking for try again using more specific criteria.</h6> : ''}
          </div>
          <div>
            <Button primary onClick={() => {
              runSearch()
            }}>Search</Button>
          </div>
        </div>
        <div className='results'>
          <StoredSplitter className='splitter' storeKey='advanced-search' enable={selected !== undefined} initialValue={0.70} >
            {table}
            {selected && <SplitterDivider />}
            {selected && item && <RenderSelection selection={convert(item)} onClose={() => setSelected(undefined)} />}
          </StoredSplitter>
          {menu && <MediaHistoryMenu state={menu} onClose={() => setMenu(undefined)} onView={(item) => setSelected(item.id)} />}
        </div>
      </SidebarView.Body>
    </SidebarView >
    </>
  }, [account, accountDescr, agent, agentDescr, contactDescr, contactId, data, dispatch, endDate, filters.months, format, item, loading, media, mediaQueueEmail, mediaQueueFacebook, mediaQueueTwitter, mediaQueueWebChat, menu, onColumnResize, onMediaType, queue, runSearch, searchTerm, selected, sorted, startDate, task, taskDescr, types, widths])

  return <>{rendor}</>
}

function taskDescription(task: Task) {
  return '[' + task.lookupId + '] ' + task.subject
}

function convert(item: MediaCallHistoryEntry, mediaTypeId?: MediaType) {
  const medidType = (!mediaTypeId) ? item.mediaTypeId : mediaTypeId
  return { type: medidType, id: item.id, isOutgoing: item.isOutgoing, notes: item.notes, ipfxId: item.ipfxId, ipfxType: item.ipfxType, accountId: item.dataSource === 'graph' ? item.accountId : '' } as ItemSelected
}

export function RenderSelectionPopup({ selection, onClose }: { selection: ItemSelected, onClose: () => void }) {
  const dispatch = useDispatch()

  useEffect(() => {
    return () => {
      dispatch(actions.renderSelectionClosed(selection.id))
    }
  }, [dispatch, selection])

  return <Modal isOpen={true} className='RenderSelectionPopup' >
    <RenderSelection selection={selection} onClose={onClose} />
  </Modal>
}

export function RenderSelection({ selection, onClose, className }: { selection: ItemSelected, onClose: () => void, className?: string }) {
  const dispatch = useDispatch()
  const [tab, setTab] = useState('item')
  const transcriptionEnabled = useSelector(s => selectConfigProp(s, 'transcriptionEnabled'))

  useEffect(() => {
    return () => {
      dispatch(actions.renderSelectionClosed(selection.id))

      //unscribescribe to transcription events
      if ((selection.type === 'CALL' || selection.type === 'VM') && transcriptionEnabled === true && tab === 'item') {
        callTranscriptionUnSubscribe(selection.id)
      }

    }
  }, [dispatch, selection.id, selection.type, tab, transcriptionEnabled])

  useEffect(() => {
    return () => {
      setTab('item')
    }
  }, [selection.id])

  //logic to work out TaskItemAction is not complete
  let action = TaskItemAction.none
  if (selection.isOutgoing) {
    action = TaskItemAction.outgoing
  }

  function tabContent() {
    if (tab === 'item') {
      switch (selection.type) {
        case 'EMAIL':
          return <EmailViewPanel className='emailViewer' emailId={selection.id} accountId={selection.accountId} />
        case 'TC':
          return <ChatViewPanel id={selection.id} />
        case 'TWEET':
          return <TweetViewPanel tweetId={selection.id} reply={selection.isOutgoing} />
        case 'FB':
          return <FacebookViewPanel activityId={selection.id} />
        case 'OUT':
          return <OutBoundViewPanel id={selection.id} />
        case 'VM':
        case 'CALL':
          if (transcriptionEnabled) {
            return <TranscriptionViewPanel mediaId={selection.id} />
          }
          return <CallViewByItemDetails itemId={selection.id} itemType={selection.type} itemAction={action} />
      }
    } else if (tab === 'details') {
      return <CallViewByItemDetails itemId={selection.id} itemType={selection.type} itemAction={action} />
    } else if (tab === 'contact' && selection.ipfxId) {
      return <div className='ContactPage'><ContactForm contactId={selection.ipfxId} contactType={ContactType.Customer} /></div>
    } else if (tab === 'transcription') {
      return <TranscriptionViewPanel mediaId={selection.id} />
    } else {
      return <></>
    }
  }

  return <ViewPanel className={className} onClose={() => onClose()}>
    <div className='tab'>
      <header>
        <TabsHeader className='tab-buttons'>
          {selection.type === 'EMAIL' && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Email</TabButton>}
          {selection.type === 'TC' && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Chat</TabButton>}
          {selection.type === 'TWEET' && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Tweet</TabButton>}
          {selection.type === 'FB' && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Facebook</TabButton>}
          {selection.type === 'OUT' && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Outbound</TabButton>}
          {transcriptionEnabled && (selection.type === 'CALL' || selection.type === 'VM') && <TabButton active={tab === 'item'} onClick={() => setTab('item')} >Transcription</TabButton>}
          {<TabButton active={tab === 'details' || (!transcriptionEnabled && (selection.type === 'CALL' || selection.type === 'VM') && tab === 'item')} onClick={() => setTab('details')} >Details</TabButton>}
          {selection.ipfxType === MediaHistoryIpfxType.Contact && <TabButton active={tab === 'contact'} onClick={() => setTab('contact')} >Contact</TabButton>}
        </TabsHeader>
      </header>
      <div className='tab-contents'>
        {tabContent()}
      </div>
    </div>
  </ViewPanel >
}

export function ViewPanel({ children, onClose, className }: React.PropsWithChildren<{ onClose(): void, className?: string }>) {
  return <div className={classNames('ViewPanel', className)}>
    <Button className='close' onClick={onClose}>X</Button>
    {children}
  </div>
}

function MediaTypeCheckbox({ types, type, onChange, label }: { types: MediaTypesFilter, type: MediaType, onChange(type: MediaType, value: boolean): void, label: string }) {
  return <Checkbox checked={types[type]} label={label} onChange={e => onChange(type, e.currentTarget.checked)} />
}

function FilterSection({ className, title, horizontal, children, button }: React.PropsWithChildren<{ title?: string, horizontal?: boolean, className?: string, button?: any }>) {
  return <section className={classNames('FilterSection', className)}>
    <div className='title'>{title && <h2>{title}</h2>}{button}</div>
    <div className={classNames('content', horizontal && 'horizontal')}>
      {children}
    </div>
  </section>
}

function getMediaColumns(shortTimeFormat: string, shortDateFormat: string, onMore?: (state: MediaMenuState) => void, columnsWidths?: ColumnWidths): Array<TableColumn<MediaCallHistoryEntry>> {
  let columns = [{
    id: 'type',
    Cell: ({ original: r }) => <GetMediaCallHistoryIcon history={r} />,
    width: 40,
    className: 'icon',
    sortable: false,
  }, {
    id: 'customer',
    Header: 'Customer name',
    Cell: ({ original: r }) => <span title={r.ipfxDescription}>{r.ipfxDescription}</span>,
    width: 120,
    sortable: false,
  }, {
    id: 'agent',
    Header: 'Agent name',
    Cell: ({ original: r }) => <span title={r.extensionName}>{r.extensionName}</span>,
    width: 120,
    sortable: false,
  }, {
    id: 'subject',
    Header: 'Subject',
    Cell: ({ original: r }) => <span title={r.subject}>{r.subject}</span>,
    sortable: false,
  }, {
    id: 'queueName',
    Header: 'Queue',
    Cell: ({ original: r }) => <span title={r.queueName}>{r.queueName}</span>,
    sortable: false,
  },
  /*{
    id: 'task',
    Header: 'Task',
    Cell: ({ original: r }) => {
      const t = !r.lookupId ? '' : r.taskSubject + " [#" + r.lookupId + "]"
      return <span title={t}> {t}</span>
    },
    width: 120,
    sortable: false,
  }, */
  {
    id: 'date',
    Header: 'Date',
    accessor: r => formatRelativeDateTime(r.receivedAtUtc, shortDateFormat, shortTimeFormat),
    width: 125,
    sortable: false,
  }] as Array<TableColumn<MediaCallHistoryEntry>>

  if (onMore) {
    columns.push({
      id: 'actions',
      Cell: ({ original: data, viewIndex }: TableRow<MediaCallHistoryEntry>) => <div className='actions'>
        <MoreButton className='menu-button' onClick={e => {
          e.stopPropagation()
          onMore({ target: targetFromEvent(e), item: data })
        }
        } />
      </div>,
      width: 40,
      sortable: false,
      resizable: false,
    })
  }

  return columns.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

function GetMediaCallHistoryIcon({ history }: { history: MediaCallHistoryEntry }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  const title = getMediaCallHIstoryTitle(history, format.shortTimeFormat, format.shortDateFormat) + (history.notes ? "\r\n\r\n" + history.notes : '')
  switch (history.mediaTypeId) {
    case "CALL":
      {
        if (history.isOutgoing) {
          return <div title={title}><PlacedIcon /></div>
        } else {
          if (history.isAnswered) {
            return <div title={title}><ReceivedIcon /></div>
          } else {
            return <div title={title}><MissedIcon /></div>
          }
        }
      }
    case "EMAIL": {
      if (history.isOutgoing) {
        return <div title={title}><EmailReplyIcon /></div>
      } else {
        return <div title={title}><EmailIcon /></div>
      }
    }
    case "FB":
      {
        return <div title={title}><FacebookIcon /></div>
      }
    case "TWEET":
      {
        return <div title={title}><TweetIcon /></div>
      }
    case "TC":
      {
        return <div title={title}><WebChatIcon /></div>
      }
    case "VM":
      {
        return <div title={title}><MediaCallBackIcon /></div>
      }
  }
  return null
}

function getMediaCallHIstoryTitle(history: MediaCallHistoryEntry, timeFormat: string, dateFormat: string) {
  const date = formatRelativeDateTime(history.receivedAtUtc, dateFormat, timeFormat)
  switch (history.mediaTypeId) {
    case "VM":
      {
        if (history.isAnswered) {
          return "Agent '" + history.extensionName + "[" + history.extensionId + "]' has procesed a callback on queue '" + history.queueName + "[" + history.queueId + "]' " + date
        } else {
          return "Agent '" + history.extensionName + "[" + history.extensionId + "]' has delete a callback from queue '" + history.queueName + "[" + history.queueId + "]' " + date
        }
      }
    case "CALL":
      {
        if (history.isOutgoing) {
          if (history.queueId === '') {
            return "Agent '" + history.extensionName + "[" + history.extensionId + "]' made an out going call " + date
          } else {
            return "Agent '" + history.extensionName + "[" + history.extensionId + "]' received a call on queue '" + history.queueName + "[" + history.queueId + "]' " + date
          }
        } else {
          if (history.isAnswered) {
            if (history.queueId === '') {
              return "Agent '" + history.extensionName + "[" + history.extensionId + "]' answered an direct call " + date
            } else {
              return "Agent '" + history.extensionName + "[" + history.extensionId + "]' received a call on queue '" + history.queueName + "[" + history.queueId + "]' " + date
            }
          } else {
            return "Agent '" + history.extensionName + "[" + history.extensionId + "]' missed a call " + date
          }
        }
      }
    case "EMAIL": {
      if (history.isOutgoing) {
        if (history.extensionId === null) {
          return "Reply sent " + date
        }
        return "Agent '" + history.extensionName + "[" + history.extensionId + "]' sent reply " + date
      } else {
        return "Email received in queue '" + history.queueId + "' was answered by Agent '" + history.extensionName + "[" + history.extensionId + "]' " + date
      }
    }
    case "FB":
      {
        return ''
      }
    case "TWEET":
      {
        return ''
      }
    case "TC":
      {
        return ''
      }
  }
  return ''
}

function MediaHistoryMenu({ state: { item, target }, onClose, onView }: { state: MediaMenuState, onClose(): void, onView(item: MediaCallHistoryEntry): void }) {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const access = useSelector(selectMyAccess)

  if (!extension || !access) return null

  const dialEnabled = (item.mediaTypeId === 'CALL' || item.mediaTypeId === 'VM') && (extension.status === ExtensionStatus.None || extension.status === ExtensionStatus.Released)
  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {access.features.phoneDial && <MenuItem disabled={!dialEnabled} onClick={() => { if (dialEnabled) dispatch(actions.ccDial(item.subject)) }}>
      <DialIcon />
      Dial
    </MenuItem>}
    {access.features.phoneDial && <MenuDivider />}
    <MenuItem onClick={() => onView(item)}>
      View
    </MenuItem>
  </Menu>
}



type EmailAccountProp = HasClassName & {
  autoFocus?: boolean
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onSelected: (item: QueueEmail) => string
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
}

export function EmailAccountLookup({ className, autoFocus, onChange, onSelected, placeholder, value, onKeyUp, onKeyDown }: EmailAccountProp) {
  const myId = useSelector(selectMyself)
  const emails = useSelector(selectSearchEmailAccounts)
  const ref = useRef<string>('')

  async function searchAccounts(input: string) {
    if (!input || !myId || !emails || ref.current === input) { return [] }

    let search = input
    let type: 'startswith' | 'contains' = 'startswith'
    if (input.indexOf('*') === 0) {
      search = search.substr(1)
      type = 'contains'
    } else if (input.indexOf('*') === search.length - 1) {
      search = search.substr(0, search.length - 1)
    }

    if (type === 'contains') {
      return emails.filter(x => x.emailAddress.indexOf(input) > 0 || search === '')
    } else {
      return emails.filter(x => x.emailAddress.indexOf(input) === 0 || x.emailAddress.indexOf(' ' + input) > -1)
    }
  }

  return <Autocomplete className={className} value={value} onKeyUp={onKeyUp} onKeyDown={onKeyDown} placeholder={placeholder} getItems={searchAccounts} autoFocus={autoFocus}
    renderItem={item => item.type === 1 ? item.azureSearchGroup : item.emailAddress} onChange={e => onChange(e.currentTarget.value)} onSelected={(item) => {
      if (onSelected) {
        const x = onSelected(item)
        ref.current = x
      }
    }} />
}

type SearchEntityProp = HasClassName & {
  autoFocus?: boolean
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onSelected: (item: SearchEntity) => string
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
}

export function AgentQueueLookup({ className, autoFocus, onChange, onSelected, placeholder, value, onKeyUp, onKeyDown }: SearchEntityProp) {
  const myId = useSelector(selectMyself)
  const ref = useRef<string>('')

  async function search(input: string) {
    if (!input || !myId || ref.current === input) { return [] }
    return await getSearchResultsForAgentQueue(myId, input)
  }

  return <Autocomplete className={className} value={value} onKeyUp={onKeyUp} onKeyDown={onKeyDown} placeholder={placeholder} getItems={search} autoFocus={autoFocus}
    renderItem={item => {
      const icon = item.entityType === EntityType.Queue ? <QueueViewIcon /> : <ExtensionViewIcon />
      return <>{icon} {item.description}</>
    }
    } onChange={e => onChange(e.currentTarget.value)} onSelected={(item) => {
      if (onSelected) {
        const x = onSelected(item)
        ref.current = x
      }
    }} />
}

type TaskEntityProp = HasClassName & {
  autoFocus?: boolean
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onSelected: (item: Task) => string
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
}

export function TaskLookup({ className, autoFocus, onChange, onSelected, placeholder, value, onKeyUp, onKeyDown }: TaskEntityProp) {
  const myId = useSelector(selectMyself)
  const ref = useRef<string>('')

  async function search(input: string) {
    if (!input || !myId || ref.current === input) { return [] }
    return await getSearchMediaTasks(myId, input)
  }

  return <Autocomplete className={className} value={value} onKeyUp={onKeyUp} onKeyDown={onKeyDown} placeholder={placeholder} getItems={search} autoFocus={autoFocus}
    renderItem={item => taskDescription(item)} onChange={e => onChange(e.currentTarget.value)} onSelected={(item) => {
      if (onSelected) {
        const x = onSelected(item)
        ref.current = x
      }
    }} />
}
